import type { GA4AnalyticsEvent } from '@/domain/shared/types'
import type { RecommendedAcco } from '@/scopes/products/domain/types'
import type { SearchResult } from '@/scopes/search/search/infrastructure/api/types'
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers'
import { formatLocations } from '@/scopes/search/search/infrastructure/api/searchResponseMapper'

export interface SearchFilterClickEventProps {
  element: 'checkbox' | 'radio button' | 'slider' | 'calendar' | 'participants selector' | 'textField'
  type: string
  result: string | number | readonly string[] | undefined
}

export function createSearchFilterClickEvent({
  element,
  type,
  result,
}: SearchFilterClickEventProps): GA4AnalyticsEvent {
  return {
    event: 'clickFilter',
    description: 'set filter on product listing',
    element,
    type,
    result: result?.toString().toLowerCase(),
    interaction: true,
  }
}

export function createSearchProductClickEvent(result: SearchResult): GA4AnalyticsEvent {
  const occupancy
    = (result.occupancyInfo?.adultsCount ?? 0)
      + (result.occupancyInfo?.childrenCount ?? 0)
      + (result.occupancyInfo?.babiesCount ?? 0)
  return {
    event: 'eec.impressionClick',
    ecommerce: {
      click: {
        actionField: {
          list: 'search result',
        },
        products: [
          {
            id: result.id,
            name: result.name,
            category: formatLocations(result.locations),
            badges: result.collection,
            accoStars: result.rating,
            rating: result.userRating,
            entryPrice: result.price?.averagePrice,
            campaignId: result.campaign?.id,
            campaignName: result.campaign?.title,
            countryId: result.locations[0].deskId,
            regionId: result.locations[1].deskId,
            cityId: result.locations[2].deskId,
            list: 'search result',
            duration: result.featuredFilters?.duration,
            totalGuests: occupancy,
            departureDate: result.departureDateDetail,
          },
        ],
      },
    },
  }
}

export function createSearchRecommendationClickEvent(acco: RecommendedAcco, isLoggedIn: boolean, brand: string) {
  const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(
    acco.price?.participants || [],
  )
  return {
    event: 'select_item',
    ecommerce: {
      item_list_id: 'reco_list_pdp_01',
      item_list_name: 'recomended accomodations on pdp',
      items: [
        {
          item_id: acco?.id,
          item_name: acco?.name,
          item_brand: brand,
          item_category: acco?.country,
          item_category2: acco?.region,
          item_category3: acco?.city,
          item_list_id: 'reco_list_pdp_01',
          item_list_name: 'recomended accomodations on pdp',
          price: acco?.price?.averagePrice,
        },
      ],
    },
    logged_in: isLoggedIn,
    accoId: acco?.id,
    transport: acco?.price?.transportType,
    boardType: acco?.price.mealplan.name,
    duration: acco?.price.durationInDays,
    partyComposition,
    totalGuests,
    departureDate: acco?.price?.departureDate,
    entryPrice: acco?.price?.averagePrice,
    campaignId: acco?.campaign?.id,
    promotedPrice: 'shown',
    cityId: acco?.locations[2]?.deskId,
    regionId: acco?.locations[1]?.deskId,
    countryId: acco?.locations[0]?.deskId,
  }
}

export function createSearchRecommendationInViewportEvent(accos: RecommendedAcco[]) {
  return {
    event: 'eec.impressionView_recommendation',
    type: 'flag',
    interaction: false,
    ecommerce: {
      impressions: accos.map((acco: RecommendedAcco, i: number) => ({
        list: 'recomended accomodations on pdp',
        id: acco?.id,
        name: acco?.name,
        duration: `${acco?.price?.durationInDays} dagen`,
        totalGuests: formatParticipantDistributionToGA4Result(acco.price?.participants || [])?.total,
        departureDate: acco?.price?.departureDate,
        category: formatLocations(acco?.locations),
        position: i + 1,
        accoStars: acco?.rating,
        badges: acco?.collection,
        rating: acco?.userRating,
        entryPrice: acco?.price?.averagePrice,
        campaignId: acco?.campaign?.id,
        campaignName: acco?.campaign?.title,
        cityId: acco?.locations[2]?.deskId,
        regionId: acco?.locations[1]?.deskId,
        countryId: acco?.locations[0]?.deskId,
      })),
    },
  }
}
